import { useState, useEffect } from 'react';
import {
  Edit,
  Save,
  X,
  RotateCcw,
  Users,
  UserMinus,
  Heart,
  Star,
  CheckSquare,
  MessageSquare,
  List,
  CalendarPlus,
  Bell,
  Calendar,
  Baby,
  Sparkles,
  Crown,
  Megaphone,
  Info,
  Layers,
} from 'lucide-react';
import { getSupabaseAdminClient } from '@parentpath/supabase-client';
import { useAdminLocale } from '../../hooks/useAdminLocale';
import { LocaleSelector } from '../../components/LocaleSelector';

interface NotificationTemplate {
  id: string;
  type: string;
  locale: string;
  title_template: string;
  message_template: string;
  available_placeholders: string[] | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

const typeLabels: Record<string, { label: string; icon: typeof Bell; color: string }> = {
  partner_connected: { label: 'Partner verbunden', icon: Users, color: 'text-green-600 bg-green-100' },
  partner_removed: { label: 'Partner entfernt', icon: UserMinus, color: 'text-red-600 bg-red-100' },
  name_match: { label: 'Namen-Match', icon: Heart, color: 'text-pink-600 bg-pink-100' },
  partner_favorited_name: { label: 'Partner hat favorisiert', icon: Star, color: 'text-yellow-600 bg-yellow-100' },
  checklist_item_completed: { label: 'Checklisten-Item erledigt', icon: CheckSquare, color: 'text-teal-600 bg-teal-100' },
  checklist_item_completed_batch: { label: 'Checklisten-Items (Zusammenfassung)', icon: Layers, color: 'text-teal-600 bg-teal-100' },
  checklist_note_added: { label: 'Checklisten-Notiz', icon: MessageSquare, color: 'text-blue-600 bg-blue-100' },
  checklist_created: { label: 'Neue Checkliste', icon: List, color: 'text-teal-600 bg-teal-100' },
  appointment_created: { label: 'Neuer Termin', icon: CalendarPlus, color: 'text-purple-600 bg-purple-100' },
  appointment_created_batch: { label: 'Termine (Zusammenfassung)', icon: Layers, color: 'text-purple-600 bg-purple-100' },
  appointment_reminder: { label: 'Terminerinnerung', icon: Bell, color: 'text-orange-600 bg-orange-100' },
  appointment_updated: { label: 'Termin geändert', icon: Calendar, color: 'text-purple-600 bg-purple-100' },
  new_ssw: { label: 'Neue SSW erreicht', icon: Baby, color: 'text-teal-600 bg-teal-100' },
  trimester_change: { label: 'Trimester-Wechsel', icon: Baby, color: 'text-teal-600 bg-teal-100' },
  welcome: { label: 'Willkommen', icon: Sparkles, color: 'text-teal-600 bg-teal-100' },
  premium_activated: { label: 'Premium aktiviert', icon: Crown, color: 'text-yellow-600 bg-yellow-100' },
  admin_broadcast: { label: 'Admin-Broadcast', icon: Megaphone, color: 'text-blue-600 bg-blue-100' },
  // Wehenzähler-Templates werden auf separater Seite verwaltet (/features/contraction-timer)
};

export function TemplatesPage() {
  const { contentLocale } = useAdminLocale();
  const [templates, setTemplates] = useState<NotificationTemplate[]>([]);
  const [germanTemplates, setGermanTemplates] = useState<NotificationTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [editMessage, setEditMessage] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    fetchTemplates();
  }, [contentLocale]);

  const fetchTemplates = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await getSupabaseAdminClient()
        .from('notification_templates')
        .select('*')
        .eq('locale', contentLocale)
        .order('type');

      if (error) throw error;
      setTemplates(data || []);

      // Load German reference templates when editing non-DE locale
      if (contentLocale !== 'de') {
        const { data: deData } = await getSupabaseAdminClient()
          .from('notification_templates')
          .select('*')
          .eq('locale', 'de')
          .order('type');
        setGermanTemplates(deData || []);
      } else {
        setGermanTemplates([]);
      }
    } catch (err) {
      console.error('Error fetching templates:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = (template: NotificationTemplate) => {
    setEditingId(template.id);
    setEditTitle(template.title_template);
    setEditMessage(template.message_template);
  };

  const handleCancel = () => {
    setEditingId(null);
    setEditTitle('');
    setEditMessage('');
  };

  const handleSave = async (templateId: string) => {
    if (!editTitle.trim() || !editMessage.trim()) {
      alert('Titel und Nachricht dürfen nicht leer sein');
      return;
    }

    setIsSaving(true);

    try {
      const { error } = await getSupabaseAdminClient()
        .from('notification_templates')
        .update({
          title_template: editTitle,
          message_template: editMessage,
          updated_at: new Date().toISOString(),
        } as never)
        .eq('id', templateId);

      if (error) throw error;

      // Lokale Liste aktualisieren
      setTemplates((prev) =>
        prev.map((t) =>
          t.id === templateId
            ? { ...t, title_template: editTitle, message_template: editMessage }
            : t
        )
      );

      setEditingId(null);
    } catch (err) {
      console.error('Error saving template:', err);
      alert('Fehler beim Speichern');
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleActive = async (template: NotificationTemplate) => {
    try {
      const { error } = await getSupabaseAdminClient()
        .from('notification_templates')
        .update({
          is_active: !template.is_active,
          updated_at: new Date().toISOString(),
        } as never)
        .eq('id', template.id);

      if (error) throw error;

      setTemplates((prev) =>
        prev.map((t) =>
          t.id === template.id ? { ...t, is_active: !t.is_active } : t
        )
      );
    } catch (err) {
      console.error('Error toggling template:', err);
    }
  };

  const handleResetToDefault = async (template: NotificationTemplate) => {
    if (!confirm(`Möchtest du den Text für "${typeLabels[template.type]?.label || template.type}" auf den Standardwert zurücksetzen?`)) {
      return;
    }

    // Standard-Texte per Locale
    const defaultsDe: Record<string, { title: string; message: string }> = {
      partner_connected: {
        title: 'Partner verbunden!',
        message: '{partner_name} hat deine Einladung angenommen',
      },
      partner_removed: {
        title: 'Partner entfernt',
        message: 'Die Partnerverbindung wurde getrennt',
      },
      name_match: {
        title: 'Es ist ein Match!',
        message: 'Ihr mögt beide den Namen "{name}"',
      },
      partner_favorited_name: {
        title: 'Name favorisiert',
        message: '{partner_name} hat "{name}" zu den Favoriten hinzugefügt',
      },
      checklist_item_completed: {
        title: 'Item erledigt',
        message: '{partner_name} hat "{item_title}" abgehakt',
      },
      checklist_item_completed_batch: {
        title: '{count} Items erledigt',
        message: '{partner_name} hat {count} Checklisten-Items abgehakt',
      },
      checklist_note_added: {
        title: 'Notiz hinzugefügt',
        message: '{partner_name} hat eine Notiz zu "{item_title}" hinzugefügt',
      },
      checklist_created: {
        title: 'Neue Checkliste',
        message: '{partner_name} hat eine neue Checkliste erstellt: "{checklist_title}"',
      },
      appointment_created: {
        title: 'Neuer Termin',
        message: '{partner_name} hat einen Termin hinzugefügt: {appointment_title}',
      },
      appointment_created_batch: {
        title: '{count} neue Termine',
        message: '{partner_name} hat {count} Termine erstellt',
      },
      appointment_reminder: {
        title: 'Terminerinnerung',
        message: '{reminder_time}: {appointment_title}',
      },
      appointment_updated: {
        title: 'Termin geändert',
        message: '{partner_name} hat "{appointment_title}" geändert',
      },
      new_ssw: {
        title: 'Neue SSW erreicht',
        message: 'Du bist jetzt in SSW {ssw}!',
      },
      trimester_change: {
        title: 'Neues Trimester',
        message: 'Willkommen im {trimester}. Trimester!',
      },
      welcome: {
        title: 'Willkommen bei ParentPath!',
        message: 'Schön, dass du da bist! Wir begleiten dich durch diese besondere Zeit.',
      },
      premium_activated: {
        title: 'Premium aktiviert',
        message: 'Dein Premium-Abo ist jetzt aktiv. Viel Spaß mit allen Features!',
      },
    };

    const defaultsEn: Record<string, { title: string; message: string }> = {
      partner_connected: {
        title: 'Partner connected!',
        message: '{partner_name} has accepted your invitation',
      },
      partner_removed: {
        title: 'Partner removed',
        message: 'The partner connection has been disconnected',
      },
      name_match: {
        title: "It's a match!",
        message: 'You both like the name "{name}"',
      },
      partner_favorited_name: {
        title: 'Name favorited',
        message: '{partner_name} added "{name}" to favorites',
      },
      checklist_item_completed: {
        title: 'Item completed',
        message: '{partner_name} checked off "{item_title}"',
      },
      checklist_item_completed_batch: {
        title: '{count} items completed',
        message: '{partner_name} checked off {count} checklist items',
      },
      checklist_note_added: {
        title: 'Note added',
        message: '{partner_name} added a note to "{item_title}"',
      },
      checklist_created: {
        title: 'New checklist',
        message: '{partner_name} created a new checklist: "{checklist_title}"',
      },
      appointment_created: {
        title: 'New appointment',
        message: '{partner_name} added an appointment: {appointment_title}',
      },
      appointment_created_batch: {
        title: '{count} new appointments',
        message: '{partner_name} created {count} appointments',
      },
      appointment_reminder: {
        title: 'Appointment reminder',
        message: '{reminder_time}: {appointment_title}',
      },
      appointment_updated: {
        title: 'Appointment changed',
        message: '{partner_name} changed "{appointment_title}"',
      },
      new_ssw: {
        title: 'New pregnancy week',
        message: 'You are now in week {ssw}!',
      },
      trimester_change: {
        title: 'New trimester',
        message: 'Welcome to trimester {trimester}!',
      },
      welcome: {
        title: 'Welcome to ParentPath!',
        message: "Great to have you here! We'll accompany you through this special time.",
      },
      premium_activated: {
        title: 'Premium activated',
        message: 'Your premium subscription is now active. Enjoy all features!',
      },
    };

    const defaults = contentLocale === 'en' ? defaultsEn : defaultsDe;

    const defaultText = defaults[template.type];
    if (!defaultText) {
      alert('Kein Standardtext für diesen Typ verfügbar');
      return;
    }

    try {
      const { error } = await getSupabaseAdminClient()
        .from('notification_templates')
        .update({
          title_template: defaultText.title,
          message_template: defaultText.message,
          updated_at: new Date().toISOString(),
        } as never)
        .eq('id', template.id);

      if (error) throw error;

      setTemplates((prev) =>
        prev.map((t) =>
          t.id === template.id
            ? { ...t, title_template: defaultText.title, message_template: defaultText.message }
            : t
        )
      );
    } catch (err) {
      console.error('Error resetting template:', err);
      alert('Fehler beim Zurücksetzen');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-2 border-teal-200 border-t-teal-600 rounded-full animate-spin" />
      </div>
    );
  }

  // Filter admin_broadcast und contraction_* aus - Wehenzähler hat eigene Seite
  const editableTemplates = templates.filter(
    (t) => t.type !== 'admin_broadcast' && !t.type.startsWith('contraction_')
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Benachrichtigungs-Texte</h1>
          <p className="text-gray-500 mt-1">
            Passe die Texte für automatische Benachrichtigungen an
          </p>
        </div>
        <LocaleSelector />
      </div>

      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
        <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-blue-700">
          <p className="font-medium">Platzhalter</p>
          <p className="mt-1">
            Verwende Platzhalter wie <code className="bg-blue-100 px-1 rounded">{'{partner_name}'}</code>,{' '}
            <code className="bg-blue-100 px-1 rounded">{'{name}'}</code> oder{' '}
            <code className="bg-blue-100 px-1 rounded">{'{item_title}'}</code>, die automatisch durch die echten Werte ersetzt werden.
          </p>
        </div>
      </div>

      {/* Templates List */}
      <div className="bg-white rounded-xl border border-gray-200 divide-y divide-gray-100">
        {editableTemplates.map((template) => {
          const typeInfo = typeLabels[template.type] || {
            label: template.type,
            icon: Bell,
            color: 'text-gray-600 bg-gray-100',
          };
          const Icon = typeInfo.icon;
          const isEditing = editingId === template.id;

          return (
            <div key={template.id} className="p-4">
              <div className="flex items-start gap-4">
                {/* Icon */}
                <div className={`p-2.5 rounded-full ${typeInfo.color}`}>
                  <Icon className="w-5 h-5" />
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="font-medium text-gray-900">{typeInfo.label}</h3>
                    {!template.is_active && (
                      <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded">
                        Deaktiviert
                      </span>
                    )}
                  </div>

                  {isEditing ? (
                    <div className="space-y-3">
                      {/* German reference when editing non-DE locale */}
                      {contentLocale !== 'de' && (() => {
                        const deTemplate = germanTemplates.find((t) => t.type === template.type);
                        return deTemplate ? (
                          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm">
                            <p className="text-xs font-medium text-amber-700 mb-1">🇩🇪 Deutscher Referenztext:</p>
                            <p className="text-amber-900 font-medium">{deTemplate.title_template}</p>
                            <p className="text-amber-800 mt-0.5">{deTemplate.message_template}</p>
                          </div>
                        ) : null;
                      })()}
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">
                          Titel
                        </label>
                        <input
                          type="text"
                          value={editTitle}
                          onChange={(e) => setEditTitle(e.target.value)}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">
                          Nachricht
                        </label>
                        <textarea
                          value={editMessage}
                          onChange={(e) => setEditMessage(e.target.value)}
                          rows={2}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent resize-none"
                        />
                      </div>
                      {(template.available_placeholders?.length ?? 0) > 0 && (
                        <div className="text-xs text-gray-500">
                          Verfügbare Platzhalter:{' '}
                          {template.available_placeholders!.map((p, i) => (
                            <span key={p}>
                              <code className="bg-gray-100 px-1 rounded">{p}</code>
                              {i < template.available_placeholders!.length - 1 && ', '}
                            </span>
                          ))}
                        </div>
                      )}
                      <div className="flex gap-2">
                        <button
                          onClick={handleCancel}
                          className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                        >
                          <X className="w-4 h-4 inline mr-1" />
                          Abbrechen
                        </button>
                        <button
                          onClick={() => handleSave(template.id)}
                          disabled={isSaving}
                          className="px-3 py-1.5 text-sm font-medium text-white bg-teal-600 hover:bg-teal-700 rounded-lg transition-colors disabled:opacity-50"
                        >
                          <Save className="w-4 h-4 inline mr-1" />
                          Speichern
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <p className="text-sm font-medium text-gray-800">
                        {template.title_template}
                      </p>
                      <p className="text-sm text-gray-500 mt-1">
                        {template.message_template}
                      </p>
                      {(template.available_placeholders?.length ?? 0) > 0 && (
                        <div className="mt-2 text-xs text-gray-400">
                          Platzhalter: {template.available_placeholders!.join(', ')}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Actions */}
                {!isEditing && (
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => handleEdit(template)}
                      className="p-2 text-gray-400 hover:text-teal-600 hover:bg-teal-50 rounded-lg transition-colors"
                      title="Bearbeiten"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleResetToDefault(template)}
                      className="p-2 text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
                      title="Auf Standard zurücksetzen"
                    >
                      <RotateCcw className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleToggleActive(template)}
                      className={`p-2 rounded-lg transition-colors ${
                        template.is_active
                          ? 'text-green-600 hover:bg-green-50'
                          : 'text-gray-400 hover:bg-gray-100'
                      }`}
                      title={template.is_active ? 'Deaktivieren' : 'Aktivieren'}
                    >
                      <div
                        className={`w-8 h-5 rounded-full transition-colors ${
                          template.is_active ? 'bg-green-500' : 'bg-gray-300'
                        }`}
                      >
                        <div
                          className={`w-4 h-4 bg-white rounded-full shadow transform transition-transform ${
                            template.is_active ? 'translate-x-3.5' : 'translate-x-0.5'
                          } mt-0.5`}
                        />
                      </div>
                    </button>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
